import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { constants } from "@qgiv/core-js";
import {
    hasActiveProductsToBeDisplayed,
    transactionReceiptHasCommitments,
} from "@qgiv/p2p-registration";
import { selectCmsControlByIdByPage } from "../../../redux/slices/cmsSettingsSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectAllProducts } from "../../../redux/slices/productsSlice";
import { selectAllP2pSettings } from "../../../redux/slices/p2pSettingsSlice";
import { selectAllProductSettings } from "../../../redux/slices/productSettingsSlice";
import {
    selectReceipt,
    selectReceiptId,
} from "../../../redux/slices/receiptSlice";
import { selectIsCms } from "../../../redux/slices/configSlice";
import ReceiptSummary from "./ReceiptSummary";

const ConnectedReceiptSummary = ({ controlId }) => {
    const {
        ENUMS: { DefaultView, ActivityRole, GiftMatchingService },
    } = constants;
    const receiptId = useSelector(selectReceiptId);
    const receipt = useSelector(selectReceipt);
    const { transaction } = receipt;
    const {
        donations = [],
        store = {},
        amount = 0,
        registrations,
    } = transaction;

    const p2pSettings = useSelector(selectAllP2pSettings);
    const { includePurchasesInGoal } = p2pSettings;

    const formSettings = useSelector(selectAllFormSettings);
    const { thirdPartyMatching, enableMatching } = formSettings;

    const productSettings = useSelector(selectAllProductSettings);
    const { offerShipping = false, deliveryInfo = "" } = productSettings;

    const products = useSelector(selectAllProducts);

    const isCms = useSelector(selectIsCms);

    const control = useSelector((state) =>
        selectCmsControlByIdByPage(
            controlId,
            DefaultView.STANDARD_REGISTRATION_RECEIPT,
            state,
        ),
    );

    const hasCommitments = transactionReceiptHasCommitments(registrations);

    const isPaymentDetailsVisible = amount > 0 || hasCommitments;

    const isStoreVisibleOnCms =
        isCms && Object.keys(store).length > 0
            ? hasActiveProductsToBeDisplayed(products)
            : false;

    let showName = true;
    let showAmount = true;
    let showMatchingWidget = false;
    let showStoreDetails = false;

    // privacy settings are all set the same, so either use donation or the first purchase
    if (donations.length > 0) {
        // use donation privacy setting if we have one
        showName = Number(donations[0].showName) === 1;
        showAmount = Number(donations[0].showAmount) === 1;
        showMatchingWidget = !!(
            !isCms &&
            enableMatching &&
            Number(thirdPartyMatching) ===
                GiftMatchingService.DOUBLE_THE_DONATION
        );
    } else if (store.cart?.length > 0 && includePurchasesInGoal) {
        // fallback to store privacy setting if there's no donation
        showName = Number(store.cart[0].showName) === 1;
        showAmount = Number(store.cart[0].showAmount) === 1;
    }

    if (offerShipping || deliveryInfo.length > 0) {
        if (!isCms && store && Object.keys(store).length > 0) {
            showStoreDetails = true;
        } else if (isCms && isStoreVisibleOnCms) {
            showStoreDetails = true;
        }
    }

    const isNFP =
        receipt &&
        receipt.transaction &&
        receipt.transaction.registrations &&
        receipt.transaction.registrations.length > 0 &&
        receipt.transaction.registrations[0].activityRole ===
            ActivityRole.NONFUNDRAISING;

    const {
        options: {
            accordionLabel,
            accordionAdditionalText,
            accordionAdditionalTextForNfps,
            printLinkLabel,
            hideAmountCallout,
            hideNameAndAmountCallout,
            hideNameCallout,
        },
    } = control;

    const receiptSummaryProps = {
        accordionLabel,
        accordionAdditionalText,
        accordionAdditionalTextForNfps,
        controlId,
        deliveryInfo,
        hideAmountCallout,
        hideNameAndAmountCallout,
        hideNameCallout,
        offerShipping,
        printLinkLabel,
        receiptId,
        isNFP,
        isPaymentDetailsVisible,
        showAmount,
        showMatchingWidget,
        showName,
        showStoreDetails,
    };

    return <ReceiptSummary {...receiptSummaryProps} />;
};

ConnectedReceiptSummary.propTypes = {
    controlId: PropTypes.number.isRequired,
};

export default ConnectedReceiptSummary;
